import React, { useState, useEffect } from 'react';
import SEO from '../../components/SEO';
import { FaTelegram, FaWhatsapp } from 'react-icons/fa';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { isValidPhoneNumber } from 'libphonenumber-js';
import './Contact.css'
import Firework from "../../components/animations/Firework";
import { useToast } from "../../context/ToastContext";
import { sendContact } from "../../api/contactApi";
import { useLocation, Link } from 'react-router-dom';

interface LocationState {
    serviceType?: string;
}

const Contact: React.FC = () => {
    const { showToast } = useToast();
    const location = useLocation();
    const state = location.state as LocationState | null;

    const [name, setName] = useState('');
    const [nameError, setNameError] = useState<string | null>(null);
    const [nameWasTouched, setNameWasTouched] = useState(false);
    const [phone, setPhone] = useState<string | undefined>();
    const [phoneError, setPhoneError] = useState<string | null>(null);
    const [phoneWasTouched, setPhoneWasTouched] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [showMessageInput, setShowMessageInput] = useState(false);
    const [message, setMessage] = useState('');
    const [showReturnLink, setShowReturnLink] = useState(false);
    const [showForm, setShowForm] = useState(false);

    useEffect(() => {
        if (state?.serviceType) {
            const defaultMessage = `Интересует услуга: ${state.serviceType}`;
            setMessage(defaultMessage);
            setShowMessageInput(true);
        }
    }, [state?.serviceType]);

    useEffect(() => {
        if (isSubmitted) {
            const timer = setTimeout(() => {
                setShowReturnLink(true);
            }, 2500);

            return () => clearTimeout(timer);
        }
    }, [isSubmitted]);

    const validateName = (value: string): string | null => {
        if (!value.trim()) {
            return 'Пожалуйста, введите ваше имя';
        } else if (value.trim().length < 2) {
            return 'Имя должно содержать не менее 2 символов';
        }
        return null;
    };

    const validatePhone = (value: string | undefined): string | null => {
        if (!value) {
            return 'Пожалуйста, введите номер телефона';
        } else if (!isValidPhoneNumber(value)) {
            return 'Некорректный номер телефона';
        }
        return null;
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        setIsLoading(true);

        // Сбрасываем ошибки
        setNameError(null);
        setPhoneError(null);

        let hasErrors = false;

        // Валидация имени с использованием нашей функции
        const nameValidationError = validateName(name);
        if (nameValidationError) {
            setNameError(nameValidationError);
            hasErrors = true;
        }

        // Валидация телефона с использованием нашей функции
        const phoneValidationError = validatePhone(phone);
        if (phoneValidationError) {
            setPhoneError(phoneValidationError);
            hasErrors = true;
        }

        if (hasErrors) {
            setIsLoading(false);
            return;
        }

        try {
            await sendContact({
                clientName: name,
                clientPhone: phone || '',
                message: message || undefined
            });
            setIsSubmitted(true);
        } catch (e) {
            showToast('Ошибка отправки. Попробуйте позже.', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="main-content-container">
            <SEO
                title="Запись на консультацию | Медицинский психолог Ольга Гальперина"
                description="Запишитесь на онлайн консультацию с медицинским психологом. Индивидуальные и семейные консультации. Связь через Telegram, WhatsApp или форму."
                keywords="запись к психологу, онлайн консультация психолога, записаться на консультацию, психолог онлайн, медицинский психолог"
                canonical="/contact"
            />
            {!isSubmitted && (
                <div className="page-header">Запись на консультацию</div>
            )}
            <div className="contact-container">
                {isLoading ? (
                    <div className="spinner"></div>
                ) : isSubmitted ? (
                    <>
                        <div className="firework-container">
                            <Firework />
                        </div>
                        <div className="success-message-container">
                            <div className="success-message">
                                Ваше сообщение отправлено
                            </div>
                            {showReturnLink && (
                                <Link to="/" className="return-link">
                                    Вернуться на главную страницу
                                </Link>
                            )}
                        </div>
                    </>
                ) : (
                    <>
                        <div className="social-buttons-container">
                            <div className="social-buttons">
                                <a href="https://t.me/olga_galperin" target="_blank" rel="noopener noreferrer" className="social-button telegram">
                                    <FaTelegram size={24} />
                                    <span>Запись в Telegram</span>
                                </a>
                                <a href="https://wa.me/972586358411" target="_blank" rel="noopener noreferrer" className="social-button whatsapp">
                                    <FaWhatsapp size={24} />
                                    <span>Запись в WhatsApp</span>
                                </a>
                            </div>

                            {!showForm ? (
                                <button
                                    type="button"
                                    className="show-form-button"
                                    onClick={() => setShowForm(true)}
                                >
                                    Заполнить форму
                                </button>
                            ) : (
                                <form onSubmit={handleSubmit} className="contact-form fade-in">
                                    <div className="input-group name-input">
                                        <input
                                            type="text"
                                            id="name"
                                            value={name}
                                            onChange={(e) => {
                                                const newValue = e.target.value;
                                                setName(newValue);
                                                // Показываем ошибку при вводе только если поле уже было в фокусе ранее
                                                if (nameWasTouched) {
                                                    setNameError(validateName(newValue));
                                                }
                                            }}
                                            onBlur={(e) => {
                                                setNameWasTouched(true);
                                                setNameError(validateName(e.target.value));
                                            }}
                                            placeholder="Имя"
                                            required
                                        />
                                        {nameError && <div className="error">{nameError}</div>}
                                    </div>
                                    <div className="input-group phone-input">
                                        <PhoneInput
                                            id="phone"
                                            placeholder="Номер телефона"
                                            value={phone}
                                            onChange={(value) => {
                                                setPhone(value);
                                                if (phoneWasTouched && value) {
                                                    setPhoneError(validatePhone(value));
                                                }
                                            }}
                                            onBlur={() => {
                                                setPhoneWasTouched(true);
                                                setPhoneError(validatePhone(phone));
                                            }}
                                            required
                                        />
                                        {phoneError && <div className="error">{phoneError}</div>}
                                    </div>
                                    <div
                                        className={`message-toggle ${showMessageInput ? 'open' : ''}`}
                                        onClick={() => setShowMessageInput(!showMessageInput)}
                                    >
                                        {showMessageInput ? 'Скрыть поле сообщения' : 'Добавить сообщение'}
                                    </div>
                                    <div className={`input-group message-input ${showMessageInput ? 'visible' : 'hidden'}`}>
                                        <textarea
                                            id="message"
                                            value={message}
                                            onChange={(e) => setMessage(e.target.value)}
                                            placeholder="Сообщение"
                                            maxLength={2000}
                                            rows={4}
                                        />
                                    </div>
                                    <button type="submit">Отправить</button>
                                </form>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div >
    );
};

export default Contact;
